import {
 bexHttpClient
} from '../../clients';
import {
 ConnectionData,ConnectionResponse
} from '../bexService.types';

/**
 * Service class for performing Redis connection operations.
 */
class ConnectionService {
  private baseURL: string;

  constructor(baseURL = '/redis/connections') {
    this.baseURL = baseURL; // Dynamically set base URL (default is '/cm/redis/connections')
  }

  /**
   * Create a new connection.
   * @param {ConnectionData} data - The new connection data (org_id, app_id, connection_name, description, source_type, settings, advanced_options)
   * @returns {Promise<ConnectionResponse>} The response from the server returns the newly created connection.
   */

  async createConnection(data: ConnectionData): Promise<{ status: number; data: ConnectionResponse }> {
  const response = await bexHttpClient.post<ConnectionResponse>(`${this.baseURL}`, data);
  return {
    status: response.status,
    data: response.data,
  };
}

  /**
   * Update an existing connection.
   * @param {string} connectionId - The ID of the connection to update.
   * @param {ConnectionData} data - The updated connection data (connection_name, settings, etc.)
   * @returns {Promise<ConnectionResponse>} The response from the server returns the updated connection.
   */
  async updateConnection(connectionId: string, data: ConnectionData): Promise<ConnectionResponse> {
    const response = await bexHttpClient.patch<ConnectionResponse>(`${this.baseURL}/${connectionId}`, data);
    return response.data; // Return the data from the Axios response
  }

  /**
   * Get a specific connection by its ID.
   * @param {string} connectionId - The ID of the connection to fetch.
   * @param {string} oragID - The organization ID.
   * @param {string} appId - The application ID.
   * @returns {Promise<ConnectionResponse>} The response from the server returns the connection details.
   */
  async getConnectionById(connectionId: string, oragID:string, appId: string): Promise<ConnectionResponse> {
    const response = await bexHttpClient.get<ConnectionResponse>(`${this.baseURL}/${connectionId}?org_id=${oragID}&app_id=${appId}`);
    return response.data; // Return the data from the Axios response
  }

  /**
   * List all connections for the specified organization and application.
   * @param {string} oragID - The organization ID.
   * @param {string} appId - The application ID.
   * @returns {Promise<ConnectionResponse[]>} The response from the server returns a list of connections.
   */
  async getConnections(orgId: string, appId: string): Promise<ConnectionResponse[]> {
  try {
    const response = await bexHttpClient.get(`${this.baseURL}?org_id=${orgId}&app_id=${appId}`);

    // Handle both response formats: plain array or { status, data } wrapper
    if (Array.isArray(response.data)) {
      return response.data;
    }
    if (response.data?.status === 'success' && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    throw new Error('Failed to fetch connections');
  } catch (error) {
    console.error('Error fetching connections:', error);
    throw error;
  }
}

  /**
   * Delete a connection by its ID.
   * @param {string} connectionId - The ID of the connection to delete.
   * @param {string} oragID - The organization ID.
   * @param {string} appId - The application ID.
   * @returns {Promise<{ message: string }>} The response from the server returns a success message.
   */
  async deleteConnection( orgId: string, appId: string,connectionId: string): Promise<{ message: string }> {

  const response = await bexHttpClient.delete<{ message: string }>(
    `${this.baseURL}/${connectionId}?org_id=${orgId}&app_id=${appId}`
  );

  return response.data; // Return the data from the Axios response
}
 /**
   * Test a single connection/test connection by id.
   * @param {string} orgId - The organization ID.
   * @param {string} appId - The application ID.
   * @param {string} connId - The connection ID.
   * @returns {Promise<any>} The response from the server after testing the connection.
   */
async testSingleConnection(orgId: string, appId: string, connectionId: string): Promise<any> {
  const url = `${this.baseURL}/${connectionId}/test?org_id=${orgId}&app_id=${appId}`;
  const response = await bexHttpClient.post(url);
  return response.data; // Return the data from the response
}

  /**
   * Test multiple connections in bulk.
   * @param {string} orgId - The organization ID.
   * @param {string} appId - The application ID.
   * @param {string[]} connIds - The list of connection IDs to test.
   * @returns {Promise<any>} The response from the server after testing the connections in bulk.
   */
async testBulkConnections(orgId: string, appId: string, connectionIds: string[]): Promise<any> {
  // Construct the URL without the connection IDs in the URL
  const url = `${this.baseURL}/test-bulk?org_id=${orgId}&app_id=${appId}`;

  // Construct the body with the connection IDs and the orgId, appId as part of the body
  const body = {
    connection_ids: connectionIds,
  };

  // Make the POST request with the body containing the necessary information
  const response = await bexHttpClient.post(url, body);

  return response.data; // Return the data from the response
}

}

const connectionService = new ConnectionService(); // Default to '/cm/redis/connections'
export { connectionService };
